import 'dayjs/locale/fr';

import ErrorBoundaryComponent from '@cohort/merchants/components/error-pages/ErrorBoundary';
import Toaster from '@cohort/merchants/components/toasts/Toaster';
import {useAuthentication} from '@cohort/merchants/hooks/authentication';
import {useUserSessionStore} from '@cohort/merchants/hooks/stores/userSession';
import useVersionChecker from '@cohort/merchants/hooks/versionChecker';
import LoadingScreen from '@cohort/merchants/layouts/LoadingScreen';
import {languageToLocale, timeJsLocale} from '@cohort/merchants/lib/Utils';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import duration from 'dayjs/plugin/duration';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {Outlet, useSearchParams} from 'react-router-dom';

dayjs.extend(advancedFormat);
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);
dayjs.extend(duration);

const App: React.FC = () => {
  useAuthentication();
  useVersionChecker();

  const [searchParams, setSearchParams] = useSearchParams();
  const {i18n} = useTranslation();
  const isAuthenticating = useUserSessionStore(store => store.isAuthenticating);

  useEffect(() => {
    const language = searchParams.get('lng');

    if (language !== null) {
      const locale = languageToLocale(language);
      dayjs.locale(timeJsLocale(locale));
      i18n.changeLanguage(language);
      searchParams.delete('lng');
      setSearchParams(searchParams, {replace: true});
    }
  }, [i18n, searchParams, setSearchParams]);

  useEffect(() => {
    const lng = i18n.language;

    dayjs.locale(timeJsLocale(languageToLocale(lng)));
  }, [i18n.language]);

  if (isAuthenticating) {
    return <LoadingScreen isFullScreen />;
  }

  return (
    <ErrorBoundaryComponent>
      <Outlet />
      <Toaster />
    </ErrorBoundaryComponent>
  );
};

export default App;
